/**
 * General Settings Component
 *
 * Component for managing general plugin settings.
 *
 * @since 1.0.0
 */

import { Card } from '@redfoxwp/core-components/components/card';
import { Flex } from '@redfoxwp/core-components/components/flex';
import { FlexItem } from '@redfoxwp/core-components/components/flex-item';
import { LoadingSpinner } from '@redfoxwp/core-components/components/loading-spinner';
import { Select } from '@redfoxwp/core-components/components/select';
import { SettingWrapper } from '@redfoxwp/core-components/components/setting-wrapper';
import { Toggle } from '@redfoxwp/core-components/components/toggle';
import { __ } from '@wordpress/i18n';

import { useSettingsContext } from '../../../contexts/settings-context';

/**
 * General Settings component
 *
 * @since 1.0.0
 *
 * @return JSX element.
 */
export const GeneralSettings = (): JSX.Element => {
  const { settings, loading, isSaving, isResetting, updateSettings } = useSettingsContext();

  const handleToggleChange = (key: keyof typeof settings.general) => (checked: boolean) => {
    updateSettings('general', {
      ...settings?.general,
      [key]: checked,
    });
  };

  const handleSelectChange = (key: keyof typeof settings.general) => (value: string | string[]) => {
    // For single select, value will be a string, so we take the first element if it's an array
    const singleValue = Array.isArray(value) ? value[0] : value;
    updateSettings('general', {
      ...settings?.general,
      [key]: singleValue,
    });
  };

  // Show loading spinner only on initial data load
  if (loading) {
    return (
      <Card
        styleProps={{
          padding: '30px',
        }}
      >
        <LoadingSpinner
          isLoading={true}
          variant='ring'
          size='medium'
          centered={true}
          showText={true}
          text={__('Loading settings...', 'redfox-added-to-cart-popup')}
        />
      </Card>
    );
  }

  // Disable inputs during save/reset operations
  const isDisabled = isSaving || isResetting;

  return (
    <Flex direction='column' gap='20px' fullWidth>
      <FlexItem>
        <SettingWrapper
          label={__('Enable Add to Cart Popup', 'redfox-added-to-cart-popup')}
          description={__(
            'Enable or disable the popup functionality globally.',
            'redfox-added-to-cart-popup'
          )}
          styleProps={{
            containerPadding: '30px',
          }}
        >
          <Toggle
            checked={settings?.general?.enabled ?? false}
            onChange={handleToggleChange('enabled')}
            size='medium'
            disabled={isDisabled}
          />
        </SettingWrapper>
      </FlexItem>

      <FlexItem>
        <SettingWrapper
          label={__('Popup Content', 'redfox-added-to-cart-popup')}
          description={__(
            'Choose what to display in the popup when a product is added.',
            'redfox-added-to-cart-popup'
          )}
          disabled={!(settings?.general?.enabled ?? false)}
          styleProps={{
            containerPadding: '30px',
            inputAreaMaxWidth: '360px',
          }}
        >
          <Select
            options={[
              {
                value: 'just_added',
                label: __('Show Just Added Product', 'redfox-added-to-cart-popup'),
              },
              { value: 'full_cart', label: __('Show Full Cart', 'redfox-added-to-cart-popup') },
            ]}
            value={settings?.general?.display_mode ?? 'just_added'}
            onChange={handleSelectChange('display_mode')}
            placeholder={__('Select display mode...', 'redfox-added-to-cart-popup')}
            disabled={!(settings?.general?.enabled ?? false) || isDisabled}
            size='medium'
          />
        </SettingWrapper>
      </FlexItem>
    </Flex>
  );
};
